import type { JSX } from 'react'
import { mqMin, space, tk, tokPx } from '@sunwebgroup/ui-components'
import { Skeleton } from '@sunwebgroup/ui-components/uds'
import { css, styled } from 'styled-components'
import { Card } from '../../../../../../../common/components/Card'

const ProductNameSkeleton = styled(Skeleton)`
  width: 211px;
  height: ${space('primitives_space_24')};
`

const StarsSkeleton = styled(Skeleton)`
  width: 75px;
  height: ${space('primitives_space_16')};
`

const LocationsSkeleton = styled(Skeleton)`
  width: 173px;
  height: ${space('primitives_space_16')};;
`

const ImageCarouselSkeleton = styled(Skeleton)`
  width: 100%;
  height: 240px;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: 312px;
  }
`

const UserRatingSkeleton = styled(Skeleton)`
  width: 65px;
  height: ${space('primitives_space_24')};
  border-radius: ${tokPx('primitives_corner_radius_xxl')};
`

const USPListSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

const USPItemSkeleton = styled(Skeleton)`
  width: 258px;
  height: ${space('primitives_space_16')};
`

const PackageBlockAndPriceSkeleton = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tk('spacing_sm')};
  justify-content: space-between;
`

const PackageBlockFirstRowSkeleton = styled(Skeleton)`
  width: 135px;
  height: ${space('primitives_space_16')};
`

const PackageBlockSecondRowSkeleton = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${space('primitives_space_4')};
  justify-content: space-between;
  width: 135px;
  height: ${space('primitives_space_16')};

  > * {
    width: 100%;
  }
`

const PriceSkeleton = styled(Skeleton)`
  width: 122px;
  height: ${space('primitives_space_32')};
`

const TextsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_24')};
  padding: ${space('primitives_space_24')};
  justify-content: space-between;
  flex: 1;

  > * {
    margin-bottom: ${space('primitives_space_12')};
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const PackageBlockSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_4')};
  align-content: flex-end;
  align-self: flex-end;
`

const PriceBoxSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
  justify-content: space-between;
  align-items: flex-end;
`

// Merged flex containers for better reusability
const FlexColumn = styled.div<{ $gap?: any }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$gap || space('primitives_space_8')};
`

const FlexRow = styled.div<{ $gap?: any, $justify?: any }>`
  display: flex;
  flex-direction: row;
  gap: ${props => props.$gap || '0'};
  justify-content: ${props => props.$justify || 'flex-start'};
`

const CardSkeleton = styled(Card)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: flex;
  }
`

ProductCardLoading.modSize = (minWidth: 'tablet' | 'desktop') => {
  switch (minWidth) {
    case 'tablet':
      return css`

        ${ImageCarouselSkeleton} {
          max-width: 255px;
          height: auto;
        }

        ${PackageBlockAndPriceSkeleton} {
          position: relative;
        }

        ${PriceBoxSkeleton} {
          position: absolute;
          bottom: 0;
          right: 0;
        }

        ${USPListSkeleton} {
          max-width: 75%;
        }
      `
    case 'desktop':
      return css`
        ${ImageCarouselSkeleton} {
          flex-shrink: 0;
          max-width: 320px;
          height: auto;
        }
      `
  }
}

export function ProductCardLoading(): JSX.Element {
  return (
    <CardSkeleton>
      <ImageCarouselSkeleton />
      <TextsWrapper>
        <FlexColumn $gap={space('primitives_space_24')}>
          <FlexColumn $gap={space('primitives_space_8')}>
            <FlexRow $justify="space-between">
              <StarsSkeleton />
              <UserRatingSkeleton />
            </FlexRow>
            <FlexRow $gap={space('primitives_space_8')} $justify="space-between">
              <FlexColumn $gap={space('primitives_space_8')}>
                <ProductNameSkeleton />
                <LocationsSkeleton />
              </FlexColumn>
            </FlexRow>
          </FlexColumn>
          <USPListSkeleton>
            <USPItemSkeleton />
            <USPItemSkeleton />
            <USPItemSkeleton />
          </USPListSkeleton>
        </FlexColumn>
        <PackageBlockAndPriceSkeleton>
          <PackageBlockSkeleton>
            <PackageBlockFirstRowSkeleton />
            <PackageBlockSecondRowSkeleton>
              <Skeleton />
              <Skeleton />
            </PackageBlockSecondRowSkeleton>
          </PackageBlockSkeleton>
          <PriceBoxSkeleton>
            <PackageBlockFirstRowSkeleton />
            <PriceSkeleton />
          </PriceBoxSkeleton>
        </PackageBlockAndPriceSkeleton>
      </TextsWrapper>
    </CardSkeleton>
  )
}

ProductCardLoading.displayName = 'ProductCardLoading'
